import React, {useEffect, useState, useMemo, useCallback, useRef} from "react";
import {inject, observer} from "mobx-react";
import cn from "classnames";
// local
import {useTranslation} from "react-i18next";
import {ConfirmModal} from "components/ConfirmModal";
import DocumentRevisionTabs from "components/requestCard/DocumentRevisionTabs";
import {TitleIcon} from "components/TitleIcon";
import LoadDocsColumn from "components/LoadDocsColumn";
import AddLinkModal from "components/modals/AddLinkModal/AddLinkModal";
import {docLabel} from "constants/file.consts";
import {
  STANDARD_AGREEMENT_TYPES,
  revisionDocuments,
  rfpContractRevisionName,
} from "constants/buyer.consts";
import {saveDocument, removeFilesConfirmModal} from "helpers/helper";
import {IconDocumentPen, IconRules, IconLink, IconBriefcase} from "components/icons";
import AddDaluxLinkModal from "components/modals/AddDaluxLinkModal";
import {fileExtension, fileType} from "constants/file.consts";
import {showWarning} from "helpers/notifications.helper";

const RequestDocuments = inject("store")(
  observer(
    ({
      store: {buyer, auth},
      revisions,
      editable,
      needZipButton,
      disableZipButton,
      columnTitles,
      columnAddPermissions,
      allRevisionsView,
      showRevisionNameInColumn,
      customLoadDocument,
      customUpdateDocuments,
      confirmationOnRemove = true,
      showTabs,
      sellerSide,
      intoContract,
      publicView,
      addWrapperClass,
    }) => {
      const {isAddLinkModal, isAddDaluxLinkModal} = buyer;
      const {t} = useTranslation();
      const [loading, setLoading] = useState(false);
      const [, setSelectItems] = useState([]);
      const [loadColumnTitle, setLoadColumnTitle] = useState("");

      const isShowTooltip = useMemo(
        () => showTabs && editable && revisions?.length < 2,
        [showTabs, revisions?.length, editable]
      );
      const isContinuousFileList = useMemo(
        () => allRevisionsView && !showRevisionNameInColumn,
        [allRevisionsView, showRevisionNameInColumn]
      );

      // REVISIONS
      const urlParams = new URLSearchParams(window.location.search);
      const urlRevisionId = urlParams.get("revision");
      const [revisionId, setRevisionId] = useState(urlRevisionId || revisions?.[0]?.id);
      const isHighlightedRevision = useMemo(() => !!urlRevisionId, [urlRevisionId]);
      const revisionTabsRef = useRef(null);
      const revision = useMemo(
        () => revisions?.find((item) => item.id === revisionId),
        [revisions, revisionId]
      );
      const documents = useMemo(
        () => ({
          contracts: revision?.contracts ?? [],
          documents: revision?.documents ?? [],
          plans: revision?.plans ?? [],
          links: revision?.links ?? [],
          id: revision?.id,
        }),
        [revision]
      );

      const [currentColumnRevisionFiles, setCurrentColumnRevisionFiles] =
        useState(documents);

      const convertUrlToNames = (links) =>
        links.map((link) => ({
          ...link,
          name: link.url?.includes("daluxFile") ? link.url.split("/")[1] : link.url,
        }));

      const divideDaluxFilesIntoFolders = useCallback((links) => {
        const foldersWithFiles = links.reduce((list, link) => {
          const folderId = link.url.split("/")[6];
          const existingFolder = list.find((folder) => folder.id === folderId);
          return existingFolder
            ? list.map((folder) =>
                folder.id === folderId
                  ? {...folder, files: [...folder.files, link]}
                  : folder
              )
            : [...list, {id: folderId, name: link.url.split("/")[7], files: [link]}];
        }, []);
        const sortedFilesInFolders = foldersWithFiles.map((folder) => ({
          ...folder,
          files: folder.files.collatorSortObj("name"),
        }));
        const sortedFolders = sortedFilesInFolders.collatorSortObj("name");
        const flattedFolderFiles = sortedFolders.reduce(
          (list, folder) => [
            ...list,
            {id: folder.id, folderName: folder.name, filesCount: folder.files.length},
            ...(folder.files ?? []),
          ],
          []
        );
        return flattedFolderFiles;
      }, []);

      const prepareLinksList = useCallback(
        (links) => {
          const namedLinks = convertUrlToNames(links);
          const directLinks = namedLinks
            .filter((link) => !link.url?.includes("daluxFile"))
            .collatorSortObj("name");
          const daluxFilesLinks = namedLinks.filter((link) =>
            link.url?.includes("daluxFile")
          );
          const daluxFilesLinksWFolders = daluxFilesLinks.filter(
            (link) => link.url.split("/").length > 6
          );
          const dividedDaluxFileLinksIntoFolders = divideDaluxFilesIntoFolders(
            daluxFilesLinksWFolders
          );
          const daluxFileLiknsWOFolders = daluxFilesLinks
            .filter((link) => link.url.split("/").length < 6)
            .collatorSortObj("name");

          return isContinuousFileList
            ? [...daluxFilesLinks, ...directLinks]
            : [
                ...dividedDaluxFileLinksIntoFolders,
                ...daluxFileLiknsWOFolders,
                ...directLinks,
              ];
        },
        [divideDaluxFilesIntoFolders, isContinuousFileList]
      );

      const collectDocuments = useCallback(
        (key) =>
          revisions
            ?.reduce(
              (docs, item) =>
                docs.concat(
                  item[key].length
                    ? showRevisionNameInColumn
                      ? [
                          {id: item.id, revisionName: item.name},
                          ...(key === "links" ? prepareLinksList(item[key]) : item[key]),
                        ]
                      : key === "links"
                      ? prepareLinksList(item[key])
                      : item[key]
                    : []
                ),
              []
            )
            [showRevisionNameInColumn ? "noop" : "collatorSortObj"]("name"),
        [revisions, showRevisionNameInColumn, prepareLinksList]
      );

      const allDocuments = useMemo(
        () => ({
          contracts: collectDocuments("contracts"),
          documents: collectDocuments("documents"),
          plans: collectDocuments("plans"),
          links: collectDocuments("links"),
        }),
        [collectDocuments]
      );
      const files = useMemo(() => {
        return {
          contracts: allRevisionsView
            ? allDocuments?.contracts ?? []
            : documents?.contracts ?? [],
          documents: allRevisionsView
            ? allDocuments?.documents ?? []
            : documents?.documents ?? [],
          plans: allRevisionsView ? allDocuments?.plans ?? [] : documents?.plans ?? [],
          links: allRevisionsView ? allDocuments?.links ?? [] : documents?.links ?? [],
        };
      }, [allDocuments, documents, allRevisionsView]);
      // the zip is built from one revision's links on the server
      const isDaluxZipButton = useMemo(
        () =>
          sellerSide &&
          !allRevisionsView &&
          files.links.filter((item) => item.url?.includes("daluxFile")).length > 1,
        [sellerSide, allRevisionsView, files]
      );

      const handleGetContractRevision = useCallback(() => {
        const contractRevision =
          revisions?.find((item) => item.name === rfpContractRevisionName) ??
          (currentColumnRevisionFiles.name === rfpContractRevisionName
            ? {...currentColumnRevisionFiles, name: rfpContractRevisionName}
            : undefined);
        const newContractRevisionId = window.crypto.randomUUID();
        setRevisionId(contractRevision?.id ?? newContractRevisionId);
        const contractRevisionDocuments = {
          ...revisionDocuments,
          id: contractRevision?.id ?? newContractRevisionId,
        };
        const newContractRevision = {
          id: newContractRevisionId,
          name: rfpContractRevisionName,
          ...contractRevisionDocuments,
        };
        intoContract &&
          !contractRevision?.id &&
          setCurrentColumnRevisionFiles(contractRevisionDocuments);
        return contractRevision ?? newContractRevision;
      }, [revisions, intoContract, currentColumnRevisionFiles]);

      const handleAddEntity = (title, data) => {
        const currentRevision = intoContract ? handleGetContractRevision() : revision;
        setCurrentColumnRevisionFiles((prevState) => ({
          ...currentRevision,
          [docLabel[title]]: [
            ...(prevState.id === currentRevision?.id
              ? prevState[docLabel[title]]
              : currentRevision[docLabel[title]]),
            ...(Array.isArray(data) ? data : [data]),
          ],
        }));
        const rfpId = buyer.currentRequest?.id;
        const pendingFiles = (Array.isArray(data) ? data : [data]).map((file) => {
          return {
            rfpId,
            revisionId: currentRevision?.id,
            fileName: title === "LINKS" ? "LINK_ADDED" : file.name,
            url: file.url,
            action: "ADDED",
            isOriginalRevision: currentRevision?.isOriginal,
            revisionName: currentRevision?.name,
          };
        });
        buyer.addPendingFileTracks(pendingFiles);
      };

      const clickAdd = (filesData, title) => {
        setLoadColumnTitle(title);
        setLoading(true);
        const uploadFiles = filesData.map((fileData) => buyer.getUID(fileData));
        Promise.all(uploadFiles)
          .then((results) => handleAddEntity(title, results))
          .finally(() => setLoading(false));
      };

      const handleAddLink = (url) =>
        files.links.some((link) => link.url === url)
          ? showWarning(t("CANT_ADD_LINK"))
          : handleAddEntity(columnTitles.links, {id: window.crypto.randomUUID(), url});
      const handleAddDaluxLink = (urls) =>
        handleAddEntity(
          columnTitles.links,
          urls?.map((url) => ({...url, id: window.crypto.randomUUID()}))
        );

      const clickRemove = (id, title) =>
        confirmationOnRemove
          ? removeFilesConfirmModal(id, (id) => removeDocument(id, title))
          : removeDocument(id, title);

      const removeDocument = (id, title) => {
        const columnRevisionFiles = {
          ...revision,
          [docLabel[title]]: id // id === null, if we click remove all button
            ? revision[docLabel[title]].filter((val) => val.id !== id)
            : [],
        };
        const payloadRevisions = revisions.map((item) =>
          (
            allRevisionsView
              ? item[docLabel[title]]?.find((file) => file.id === id)
              : item.id === revisionId
          )
            ? {
                ...item,
                [docLabel[title]]: id // id === null, if we click remove all button
                  ? item[docLabel[title]].filter((val) => val.id !== id)
                  : [],
              }
            : item
        );

        const rfpId = buyer.currentRequest?.id;
        const targetRevision = allRevisionsView
          ? revisions?.find((item) =>
              item[docLabel[title]]?.some((file) => file.id === id)
            )
          : revision;
        const removedFiles = id
          ? targetRevision?.[docLabel[title]]?.filter((val) => val.id === id) ?? []
          : targetRevision?.[docLabel[title]] ?? [];

        const pendingFiles = removedFiles.map((file) => {
          return {
            rfpId,
            revisionId: targetRevision?.id,
            fileName: title === "LINKS" ? "LINK_DELETED" : file.name,
            url: file.url,
            action: "DELETED",
            isOriginalRevision: targetRevision?.isOriginal,
            revisionName: targetRevision?.name,
          };
        });
        buyer.addPendingFileTracks(pendingFiles);

        setCurrentColumnRevisionFiles(columnRevisionFiles);
        buyer.updateCurrentRequestInfo(
          {
            documents: payloadRevisions,
          },
          true
        );
      };

      const removeLink = (id, title) =>
        id
          ? removeDocument(id, title)
          : ConfirmModal({
              title: t("WANT_DELETE_ALL_LINKS"),
              text: t("CANT_DELETE_ALL_LINKS"),
              type: "warning",
              onOk: () => removeDocument(id, title),
            });

      const loadDaluxFile = async (url) => {
        const urlData = url.split("/");
        let fileName = urlData[1];
        const data = await buyer.getDaluxProjectFileAreaFile(
          urlData[2],
          urlData[3],
          urlData[4],
          revisionId
        );
        if (!data) return;
        saveDocument(
          data,
          fileType[fileExtension.STREAM],
          fileName,
          t("FILE_DOWNLOADED")
        );
      };

      useEffect(() => {
        buyer
          .getAgreements()
          .then((response) =>
            setSelectItems([
              STANDARD_AGREEMENT_TYPES.NO_STANDARD_AGREEMENT,
              ...response.data,
              STANDARD_AGREEMENT_TYPES.OTHER_AGREEMENT,
            ])
          );
      }, [buyer]);

      // the link modals live in the store, so they would survive their own host
      // and reappear the next time it is mounted
      useEffect(
        () => () => {
          buyer.setAddLinkModalStatus(false);
          buyer.setAddDaluxLinkModalStatus(false);
        },
        [buyer]
      );

      // Effect to select revision from URL or fall back to first revision
      useEffect(() => {
        if (!revisions?.length) return;
        const urlParams = new URLSearchParams(window.location.search);
        const urlRevisionId = urlParams.get("revision");
        if (urlRevisionId && revisions.some((rev) => rev.id === urlRevisionId)) {
          setRevisionId(urlRevisionId);
        } else {
          setRevisionId((prev) =>
            prev && revisions.some((rev) => rev.id === prev) ? prev : revisions[0].id
          );
        }
      }, [revisions]);

      // Scroll to revisions section when urlRevisionId is present
      useEffect(() => {
        if (!urlRevisionId) return;
        if (!revisions?.some((rev) => rev.id === urlRevisionId)) return;
        const timeout = setTimeout(() => {
          revisionTabsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
        }, 100);
        return () => clearTimeout(timeout);
      }, [urlRevisionId, revisions]);

      useEffect(
        () => {
          if (!editable) return;
          const currentRevision = intoContract ? handleGetContractRevision() : revision;
          const payloadRevision = {
            ...currentRevision,
            ...(currentRevision?.id === currentColumnRevisionFiles.id
              ? currentColumnRevisionFiles
              : {}),
          };
          !!customUpdateDocuments && customUpdateDocuments(payloadRevision);
          buyer.updateCurrentRequestInfo({
            documents: [
              ...(revisions?.filter((item) => item.id !== currentRevision?.id) ?? []),
              payloadRevision,
            ],
          });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentColumnRevisionFiles, editable]
      );

      const isTemplateShow = useMemo(() => {
        if (intoContract || editable) {
          return (
            !sellerSide && auth?.user?.ActiveOrganization?.contractPreparationCustomLink
          );
        }
        return false;
      }, [
        editable,
        sellerSide,
        intoContract,
        auth?.user?.ActiveOrganization?.contractPreparationCustomLink,
      ]);

      const contractType = isTemplateShow && (
        <a
          href={auth.user.ActiveOrganization.contractPreparationCustomLink}
          target="_blank"
          rel="noopener noreferrer"
          className="df-row-center mt-4 link"
        >
          {t("OUR_TEMPLATES")}
        </a>
      );

      return (
        <React.Fragment>
          {!allRevisionsView && (
            <div ref={revisionTabsRef}>
              <TitleIcon
                title={t("RFP_REQUEST_TAB_DOCUMENTS_SECTION_TITLE")}
                addClass="mt-8"
                icon={<IconBriefcase />}
                tooltip={
                  isShowTooltip
                    ? "RFP_REQUEST_TAB_DOCUMENTS_SECTION_TITLE_TOOLTIP"
                    : undefined
                }
                prefixTooltipContent={
                  showTabs ? (
                    <DocumentRevisionTabs
                      isHighlightedRevision={isHighlightedRevision}
                      highlightedRevisionId={urlRevisionId}
                      revisions={revisions}
                      revisionId={revisionId}
                      isEditing={editable}
                      onChangeTab={setRevisionId}
                    />
                  ) : null
                }
              />
            </div>
          )}
          <div
            className={cn("load-docs_wrapper", {[addWrapperClass]: !!addWrapperClass})}
          >
            <LoadDocsColumn
              title={columnTitles.contracts}
              docPlaceholder="PLACEHOLDER_BUYER_CONTRACT_COLUMN"
              titleIcon={<IconDocumentPen />}
              button="CLICK_OR_DRAG_HERE"
              editable={editable}
              docs={files?.contracts}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              canAddPermissions={columnAddPermissions?.contracts}
              needZipButton={needZipButton}
              disableZipButton={disableZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              customFileTypes={[fileExtension.PDF]}
              customFileSize={102400}
              loadColumnTitle={loadColumnTitle}
              bottomChildren={!publicView && contractType}
              customLoadDocument={customLoadDocument?.contracts}
            />
            <LoadDocsColumn
              title={columnTitles.documents}
              docPlaceholder="PLACEHOLDER_BUYER_DOCUMENT_COLUMN"
              titleIcon={<IconDocumentPen />}
              button="CLICK_OR_DRAG_HERE"
              docs={files.documents}
              editable={editable}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              canAddPermissions={columnAddPermissions?.documents}
              needZipButton={needZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              customFileSize={102400}
              loadColumnTitle={loadColumnTitle}
              customLoadDocument={customLoadDocument?.documents}
            />
            <LoadDocsColumn
              title={columnTitles.plans}
              docPlaceholder="PLACEHOLDER_BUYER_DOCUMENT_COLUMN"
              titleIcon={<IconRules />}
              button="CLICK_OR_DRAG_HERE"
              docs={files.plans}
              editable={editable}
              addFile={clickAdd}
              removeFile={clickRemove}
              canRemoveAllFiles
              canAddPermissions={columnAddPermissions?.plans}
              needZipButton={needZipButton}
              loading={loading}
              allRevisionsView={allRevisionsView}
              customFileSize={102400}
              loadColumnTitle={loadColumnTitle}
              customLoadDocument={customLoadDocument?.plans}
            />
            <LoadDocsColumn
              title={columnTitles.links}
              docPlaceholder="PLACEHOLDER_BUYER_LINKS_COLUMN"
              titleIcon={<IconLink />}
              button="ADD"
              wide
              docs={
                allRevisionsView && showRevisionNameInColumn
                  ? files.links
                  : prepareLinksList(files.links)
              }
              editable={editable}
              removeFile={removeLink}
              canRemoveAllFiles
              canAddPermissions={columnAddPermissions?.links}
              loading={loading}
              allRevisionsView={allRevisionsView}
              customAdd={() => buyer.setAddLinkModalStatus(true)}
              linksView
              revisionId={revisionId}
              druggable={false}
              customFileTypes={[]}
              needZipButton={needZipButton && isDaluxZipButton}
              customLoadDocument={loadDaluxFile}
              loadColumnTitle={loadColumnTitle}
              isOpenModal={isAddLinkModal}
              modal={<AddLinkModal addLink={handleAddLink} />}
            />
          </div>
          {isAddDaluxLinkModal && (
            <AddDaluxLinkModal revisionId={revisionId} addLink={handleAddDaluxLink} />
          )}
        </React.Fragment>
      );
    }
  )
);

export default RequestDocuments;
